'use client'

import { useState, useEffect, useCallback } from 'react'

interface UseScrollDirectionOptions {
  /** Minimum scroll amount before triggering direction change (default: 10) */
  threshold?: number
  /** Initial visibility state (default: true) */
  initialVisible?: boolean
}

/**
 * Hook to detect scroll direction and control navbar visibility.
 * Returns true when navbar should be visible (scrolling up or at top),
 * false when it should be hidden (scrolling down).
 */
export function useScrollDirection({
  threshold = 10,
  initialVisible = true,
}: UseScrollDirectionOptions = {}) {
  const [isVisible, setIsVisible] = useState(initialVisible)
  const [lastScrollY, setLastScrollY] = useState(0)

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY

    // Always show navbar when at the top of the page
    if (currentScrollY < threshold) {
      setIsVisible(true)
      setLastScrollY(currentScrollY)
      return
    }

    const scrollDiff = currentScrollY - lastScrollY

    // Only trigger if scroll amount exceeds threshold
    if (Math.abs(scrollDiff) < threshold) {
      return
    }

    // Scrolling down - hide navbar
    if (scrollDiff > 0) {
      setIsVisible(false)
    }
    // Scrolling up - show navbar
    else {
      setIsVisible(true)
    }

    setLastScrollY(currentScrollY)
  }, [lastScrollY, threshold])

  useEffect(() => {
    // Set initial scroll position
    setLastScrollY(window.scrollY)

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return isVisible
}
